import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Menu, Bell, ChevronRight, Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { UserAvatar } from '@/components/common/UserAvatar';
import { useUnreadCount } from '@/hooks/useNotifications';
import { useGroups } from '@/hooks/useGroups';
import { sortGroupsForUser } from '@/lib/sortGroups';
import { GroupPickerDialog } from '@/components/expenses/GroupPickerDialog';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

interface HeaderProps {
  onMenuClick: () => void;
}

// Translation keys per static route (translated at render time)
const routeTitleKeys: Record<string, string> = {
  '/dashboard': 'web:sidebar.dashboard',
  '/profile': 'common:tab.profile',
  '/profile/edit': 'web:header.editProfile',
  '/notifications': 'web:header.notifications',
};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function Header({ onMenuClick }: HeaderProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const unreadCount = useUnreadCount();
  const queryClient = useQueryClient();
  const { data: groups } = useGroups();
  const [pickerOpen, setPickerOpen] = useState(false);

  const sortedGroups = useMemo(
    () => (groups && user ? sortGroupsForUser(groups, user.id) : groups),
    [groups, user]
  );

  const hasGroups = !!sortedGroups && sortedGroups.length > 0;

  const handleAddExpense = () => {
    if (!sortedGroups || sortedGroups.length === 0) return;
    if (sortedGroups.length === 1) {
      navigate(`/groups/${sortedGroups[0].id}/add`);
      return;
    }
    setPickerOpen(true);
  };

  // Resolve a UUID segment to a human-readable name from cached data
  const resolveSegment = (segment: string, parentSegment: string | undefined): string | null => {
    if (!UUID_REGEX.test(segment)) return null;

    if (parentSegment === 'groups') {
      // Look up group name from cached groups list
      const groups = queryClient.getQueryData<Array<{ id: string; name: string }>>(['groups', user?.id]);
      const group = groups?.find(g => g.id === segment);
      if (group) return group.name;

      // Also check individual group detail cache
      const detail = queryClient.getQueryData<{ id: string; name: string }>(['group', segment]);
      if (detail) return detail.name;
    }

    if (parentSegment === 'expenses') {
      const expense = queryClient.getQueryData<{ id: string; description: string }>(['expense', segment]);
      if (expense) return expense.description;
    }

    return null;
  };

  // Resolve expense's parent group from cache
  const resolveExpenseGroup = (expenseId: string): { id: string; name: string } | null => {
    const expense = queryClient.getQueryData<{ id: string; group_id: string; description: string }>(['expense', expenseId]);
    if (!expense?.group_id) return null;

    const groups = queryClient.getQueryData<Array<{ id: string; name: string }>>(['groups', user?.id]);
    const group = groups?.find(g => g.id === expense.group_id);
    if (group) return group;

    const detail = queryClient.getQueryData<{ id: string; name: string }>(['group', expense.group_id]);
    return detail ?? null;
  };

  // Build breadcrumbs from path
  const pathParts = location.pathname.split('/').filter(Boolean);
  let breadcrumbs: { label: string; path: string; isLast: boolean }[];

  // Contextual breadcrumbs for expense routes: Groups > GroupName > ExpenseName [> Edit]
  if (pathParts[0] === 'expenses' && pathParts[1] && UUID_REGEX.test(pathParts[1])) {
    const expenseId = pathParts[1];
    const expense = queryClient.getQueryData<{ id: string; description: string }>(['expense', expenseId]);
    const group = resolveExpenseGroup(expenseId);
    const isEdit = pathParts[2] === 'edit';

    breadcrumbs = [
      { label: t('common:tab.groups'), path: '/dashboard', isLast: false },
    ];

    if (group) {
      breadcrumbs.push({ label: group.name, path: `/groups/${group.id}`, isLast: false });
    }

    breadcrumbs.push({
      label: expense?.description ?? t('web:groupDetailPage.expense'),
      path: `/expenses/${expenseId}`,
      isLast: !isEdit,
    });

    if (isEdit) {
      breadcrumbs.push({ label: t('common:button.edit'), path: `/expenses/${expenseId}/edit`, isLast: true });
    }
  } else {
    breadcrumbs = pathParts.map((part, index) => {
      const path = '/' + pathParts.slice(0, index + 1).join('/');
      const parentSegment = index > 0 ? pathParts[index - 1] : undefined;
      const resolved = resolveSegment(part, parentSegment);
      const routeTitle = routeTitleKeys[path] ? t(routeTitleKeys[path]) : undefined;
      const label = routeTitle ?? resolved ?? part.charAt(0).toUpperCase() + part.slice(1);
      return { label, path, isLast: index === pathParts.length - 1 };
    });
  }

  return (
    <>
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border-primary bg-surface-primary px-4 lg:px-6">
      {/* Mobile menu button */}
      <button
        onClick={onMenuClick}
        className="rounded-md p-2 text-text-tertiary hover:bg-surface-hover lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1 text-sm">
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.path} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-text-quaternary" />}
            {crumb.isLast ? (
              <span className="font-medium text-text-primary">{crumb.label}</span>
            ) : (
              <button
                onClick={() => navigate(crumb.path)}
                className="text-text-tertiary hover:text-text-secondary"
              >
                {crumb.label}
              </button>
            )}
          </span>
        ))}
      </nav>

      {/* Right side */}
      <div className="ml-auto flex items-center gap-3">
        <button
          onClick={handleAddExpense}
          disabled={!hasGroups}
          title={hasGroups ? t('expenses:screen.addExpense') : t('web:header.createAGroupFirst')}
          className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">{t('expenses:screen.addExpense')}</span>
        </button>

        <div className="hidden md:block">
          <LanguageSwitcher />
        </div>

        <button
          onClick={() => navigate('/notifications')}
          className="relative rounded-md p-2 text-text-tertiary hover:bg-surface-hover"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        <button
          onClick={() => navigate('/profile')}
          className="transition-colors"
        >
          <UserAvatar name={profile?.display_name} avatarUrl={profile?.avatar_url} size="md" />
        </button>
      </div>
    </header>

    <GroupPickerDialog
      open={pickerOpen}
      onClose={() => setPickerOpen(false)}
      onPick={(id) => {
        setPickerOpen(false);
        navigate(`/groups/${id}/add`);
      }}
      groups={sortedGroups ?? []}
    />
    </>
  );
}
